/**
 * Print-optimized Maturity Assessment Results Page
 *
 * This component renders the full PDF-ready document so it can be opened
 * directly in the browser for printing or downloaded via the in-app export.
 */

import { useParams } from "wouter";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { generateMaturityResults } from "@/utils/maturityBanding";
import MaturityReportDocument from "./components/MaturityReportDocument";
import type { MaturityResults } from "@shared/interfaces/maturityResultsInterface";
import type { MaturityAssessmentRecord } from "@shared/interfaces/maturityAssessment.interface";
import type { ProjectPayload } from "@shared/interfaces/projectPayload.interface";
import type { MaturitySegment } from "@shared/interfaces/maturitySegment.interface";
import type { RecommendedAction } from "@shared/interfaces/recommendedAction.interface";

const extractAnswers = (assessment: any): number[] => {
  if (!assessment?.data) return [];

  if (Array.isArray(assessment.data)) {
    return assessment.data;
  }

  if (assessment.data.answers && Array.isArray(assessment.data.answers)) {
    return assessment.data.answers;
  }

  return [];
};

export default function MaturityPrintPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [maturityResults, setMaturityResults] = useState<MaturityResults | null>(null);

  const { data: projectData } = useQuery<ProjectPayload | null>({
    queryKey: ['/api/projects', projectId],
    queryFn: async () => {
      if (!projectId) return null;
      const response = await fetch(`/api/projects/${projectId}`);
      if (!response.ok) throw new Error('Failed to fetch project data');
      return response.json();
    },
    enabled: !!projectId,
  });

  const { data: assessmentData } = useQuery<MaturityAssessmentRecord[] | null>({
    queryKey: [`/api/maturity/${projectId}`],
    queryFn: async () => {
      if (!projectId) return null;
      const response = await fetch(`/api/maturity/${projectId}`);
      if (!response.ok) throw new Error('Failed to fetch maturity results');
      return response.json();
    },
    enabled: !!projectId,
  });

  const latestAssessment = assessmentData && Array.isArray(assessmentData) && assessmentData.length > 0
    ? assessmentData[0]
    : null;

  useEffect(() => {
    if (!latestAssessment) return;
    const answers = extractAnswers(latestAssessment);
    if (answers.length === 0) return;

    const operationalScore = answers.slice(0, 6).reduce((sum: number, score: number) => sum + score, 0);
    const strategicScore = answers.slice(6, 11).reduce((sum: number, score: number) => sum + score, 0);

    setMaturityResults(generateMaturityResults(operationalScore, strategicScore));
  }, [latestAssessment]);

  const { data: maturitySegmentData } = useQuery<MaturitySegment | null>({
    queryKey: maturityResults?.operationalBand && maturityResults?.strategicBand
      ? ['/api/maturity-segment', maturityResults.operationalBand, maturityResults.strategicBand]
      : [],
    queryFn: async () => {
      if (!maturityResults?.operationalBand || !maturityResults?.strategicBand) return null;
      const params = new URLSearchParams({
        operational: maturityResults.operationalBand,
        strategic: maturityResults.strategicBand,
      });
      const response = await fetch(`/api/maturity-segment?${params}`);
      if (!response.ok) return null;
      return response.json();
    },
    enabled: !!(maturityResults?.operationalBand && maturityResults?.strategicBand),
  });

  const { data: recommendedActionsData } = useQuery<RecommendedAction[] | null>({
    queryKey: maturitySegmentData?.id ? ['/api/recommended-actions', maturitySegmentData.id] : [],
    queryFn: async () => {
      if (!maturitySegmentData?.id) return [];
      const response = await fetch(`/api/recommended-actions?segmentId=${maturitySegmentData.id}`);
      if (!response.ok) return [];
      return response.json();
    },
    enabled: !!maturitySegmentData?.id,
  });

  const answers = useMemo(() => (latestAssessment ? extractAnswers(latestAssessment) : []), [latestAssessment]);
  const operationalAnswers = answers.slice(0, 6);
  const strategicAnswers = answers.slice(6, 11);

  const clientName = latestAssessment?.contact_company
    || latestAssessment?.contact_name
    || projectData?.name
    || 'Assessment Report';

  const exportDate = useMemo(() => new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }), []);

  if (!maturityResults || !latestAssessment) {
    return <div className="p-8 text-slate-600">Loading...</div>;
  }

  return (
    <div className="w-full flex justify-center bg-slate-100 py-10">
      <MaturityReportDocument
        maturityResults={maturityResults}
        clientName={clientName}
        projectName={projectData?.name}
        exportDate={exportDate}
        operationalAnswers={operationalAnswers}
        strategicAnswers={strategicAnswers}
        maturitySegmentData={maturitySegmentData as MaturitySegment | null}
        recommendedActionsData={recommendedActionsData as RecommendedAction[] | null}
      />
    </div>
  );
}
